import { useState } from 'react';
import { Button } from '@athena/forge';
import { useWidgetContext } from '../context/WidgetContext';
import { forgotUsername } from '../../utils/password-reset-api';
import AmplitudeUtils from '../../utils/amplitude-utils';
import WidgetFormField from '../common/WidgetFormField';
import Header from '../common/Header';
import { ERROR_KEYS, ERROR_MESSAGES } from '../../constants';

interface ForgotUsernamePageProps {
  onSuccess: (email: string) => void;
}

export default function ForgotUsernamePage({ onSuccess }: ForgotUsernamePageProps) {
  const [ email, setEmail ] = useState('');
  const [ error, setError ] = useState('');
  const [ loading, setLoading ] = useState(false);
  const { settings } = useWidgetContext();

  const handleSubmit = async () => {
    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      AmplitudeUtils.logCustomEvent('ForgotUsername::EmailSubmitted', { email });

      await forgotUsername({
        email,
        clientId: settings.clientId || '',
      });

      // If we reach here, the request was successful
      onSuccess(email);
    }
    catch (error: any) {
      AmplitudeUtils.logCustomEvent('ForgotUsername::ForgotUsernameFailed', {
        email,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        error,
      });
      setError(ERROR_MESSAGES[ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR]);
    }
    finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="forgot-username-page">
      <Header
        headerText="Password Reset"
        description="Please enter your email below."
        fontSize="large"
      />

      <WidgetFormField
        labelText="Email"
        value={email}
        type="email"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
        error={error}
        disabled={loading}
        onKeyPress={handleKeyPress}
        id="athena-forgot-username-email"
        autoComplete="email"
      />

      <Button
        text="Next"
        variant="primary"
        size="large"
        fullWidth
        onClick={handleSubmit}
        disabled={loading}
        className="fe_u_margin--top-medium"
      />

      <p className="fe_u_margin--top-medium fe_u_font-size--small fe_u_color--text-muted">
        If you don't know the email address associated with your account, please contact your administrator.
      </p>
    </div>
  );
}

